import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toolbar, Item as ToolbarItem } from 'devextreme-react/toolbar';
import Button from 'devextreme-react/button';
import ScrollView from 'devextreme-react/scroll-view';

import {
  CardActivities,
  CardNotes,
  CardTasks,
  WorkTaskTable,
} from '../../components';

import { Relation } from '../../types/crm-contact';
import { Notes } from '../../types/card-notes';
import {
  getRelationByKey,
  getNotes,
} from '../../api/relations';
import { useAuth } from '../../contexts/auth';
import './crm-contact-details.scss';

export interface State {
  relationOid: string;
  backTo: string;
}

export const CRMContactDetails = () => {
  const [data, setData] = useState<Relation>();
  const [notes, setNotes] = useState<Notes>();
  const [notesCount, setNotesCount] = useState(0);
  const [remarks, setRemarks] = useState<Notes>();
  const [remarksCount, setRemarksCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const state = useLocation().state as State;
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  function isNullOrEmpty(str: string | undefined | null) {
    return !str || str.trim() === '';
  }

  const loadData = useCallback(() => {
    setIsLoading(true);
    Promise.all([
      getRelationByKey(state.relationOid, user?.token).then((d) => setData(d)),
      getNotes(state.relationOid, user?.token).then((d) => {
        const mapped: Notes = d.map((item) => ({
          text: item.Remark,
          date: item.MaintenanceDate,
          manager: item.DoneByName,
          quotationSent: item.QuotationSent,
          remarkDone: item.RemarkDone,
          moreWorkNeeded: item.MoreWorkNeeded,
        }));
        setNotes(mapped.filter((o) => o.moreWorkNeeded));
        setNotesCount(d.filter((o) => !o.QuotationSent && o.MoreWorkNeeded).length);
        setRemarks(mapped.filter((o) => !o.moreWorkNeeded && !isNullOrEmpty(o.text)));
        setRemarksCount(d.filter((o) => !o.MoreWorkNeeded && !o.RemarkDone && !isNullOrEmpty(o.Remark)).length);
      }),
    ])
      .finally(() => setIsLoading(false));
  }, []);

  const refresh = useCallback(() => {
    loadData();
  }, []);

  const goBackClick = useCallback(() => {
    if (state.backTo === 'taskList') {
      navigate('/maintenance');
    } else if (state.backTo === 'dashboard') {
      navigate('/analytics-dashboard');
    } else {
      navigate('/crm-contact-list');
    }
  }, []);

  const fullAddress =
    data && (data.Address || data.City)
      ? [
          [data.Address, data.AddressNr].filter(Boolean).join(' '),
          [data.ZipCode, data.City].filter(Boolean).join(' '),
        ]
          .filter(Boolean)
          .join(', ')
      : '-';

  return (
    <ScrollView className='view-wrapper-scroll'>
      <div className='view-wrapper view-wrapper-contact-details'>
        <Toolbar className='toolbar-details'>
          <ToolbarItem location='before'>
            <Button icon='arrowleft' stylingMode='text' onClick={goBackClick} />
          </ToolbarItem>
          <ToolbarItem location='before'>
            <span className='toolbar-title'>{data?.Name ?? 'Laden...'}</span>
          </ToolbarItem>
          <ToolbarItem location='after' locateInMenu='auto'>
            <Button text='Vernieuwen' icon='refresh' stylingMode='text' onClick={refresh} />
          </ToolbarItem>
        </Toolbar>

        {isLoading ? (
          <div className='loading-placeholder'>Laden...</div>
        ) : data ? (
          <>
            {/* ── Summary banner ── */}
            <div className='summary-banner'>
              <div className='banner-item'>
                <i className='dx-icon dx-icon-home banner-icon' />
                <span className='banner-name'>{data.Name}</span>
              </div>
              {data.City && (
                <div className='banner-item'>
                  <i className='dx-icon dx-icon-map banner-icon' />
                  <span>{data.City}</span>
                </div>
              )}
              {data.TelephoneNumber && (
                <div className='banner-item'>
                  <i className='dx-icon dx-icon-tel banner-icon' />
                  <span>{data.TelephoneNumber}</span>
                </div>
              )}
              {data.Email && (
                <div className='banner-item'>
                  <i className='dx-icon dx-icon-email banner-icon' />
                  <span>{data.Email}</span>
                </div>
              )}
              <button
                className='banner-expand-btn'
                onClick={() => setDetailsExpanded(!detailsExpanded)}
              >
                <i className={`dx-icon dx-icon-${detailsExpanded ? 'chevronup' : 'chevrondown'}`} />
                <span>{detailsExpanded ? 'Verbergen' : 'Details'}</span>
              </button>
            </div>

            {/* ── Stacked cards ── */}
            <div className='panels'>
              {/* Relatie gegevens */}
              {detailsExpanded && (
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-card card-header-icon' />
                  <span>Relatie gegevens</span>
                </div>
                <div className='info-grid'>
                  <div className='info-row'>
                    <span className='info-label'>Relatie Nr</span>
                    <span className='info-value accent'>{data.CustomerNumber || '-'}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Bedrijf</span>
                    <span className='info-value'>{data.Name || '-'}</span>
                  </div>
                  {data.Company && (
                    <div className='info-row'>
                      <span className='info-label'>Bedrijfsnaam</span>
                      <span className='info-value'>{data.Company}</span>
                    </div>
                  )}
                  <div className='info-row'>
                    <span className='info-label'>Contactpersoon</span>
                    <span className='info-value'>{data.ContactPerson || '-'}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Adres</span>
                    <span className='info-value'>{fullAddress}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Telefoon</span>
                    <span className='info-value'>{data.TelephoneNumber || '-'}</span>
                  </div>
                  <div className='info-row'>
                    <span className='info-label'>Email</span>
                    <span className='info-value'>{data.Email || '-'}</span>
                  </div>
                  {data.InvoiceEmail && (
                    <div className='info-row'>
                      <span className='info-label'>Factuur email</span>
                      <span className='info-value'>{data.InvoiceEmail}</span>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Opdrachten */}
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-toolbox card-header-icon' />
                  <span>Opdrachten</span>
                </div>
                <div className='card-body'>
                  <WorkTaskTable isLoading={false} relationOid={state.relationOid} backTo='crmDetails' />
                </div>
              </div>

              {/* Contracten */}
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-docfile card-header-icon' />
                  <span>Contracten</span>
                </div>
                <div className='card-body'>
                  <CardTasks isLoading={isLoading} relationOid={state.relationOid} />
                </div>
              </div>

              {/* Documenten */}
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-folder card-header-icon' />
                  <span>Documenten</span>
                </div>
                <div className='card-body'>
                  <CardActivities relationOid={state.relationOid} />
                </div>
              </div>

              {/* Offerte aanvraag */}
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-warning card-header-icon' />
                  <span>Offerte aanvraag</span>
                  {notesCount > 0 && <span className='badge'>{notesCount}</span>}
                </div>
                <div className='card-body'>
                  <CardNotes items={notes} />
                </div>
              </div>

              {/* Opmerkingen */}
              <div className='dx-card info-card'>
                <div className='card-header'>
                  <i className='dx-icon dx-icon-comment card-header-icon' />
                  <span>Opmerkingen</span>
                  {remarksCount > 0 && <span className='badge'>{remarksCount}</span>}
                </div>
                <div className='card-body'>
                  <CardNotes items={remarks} />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className='loading-placeholder'>Relatie niet gevonden</div>
        )}
      </div>
    </ScrollView>
  );
};
